import { canTakeFocus } from "@/lib/returnFocus";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface LegalAcceptanceDialogProps {
  open: boolean;
  isSubmitting: boolean;
  errorMessage: string | null;
  onAccept: () => void;
  onDismiss: () => void;
  /**
   * FP-112: this dialog mounts already open after a 409, when the connect button
   * that caused it is disabled and no longer focused, so the shared dialog has no
   * opener to return to. The caller names it.
   */
  returnFocusTo?: () => HTMLElement | null;
  /** Why it is asking now. Defaults to the bank-connect wording. */
  description?: string;
  /**
   * G42: only Accept and Not now answer it. Esc, a click outside and the close button do
   * nothing, because walking away from the prompt is not a choice (the sign-up prompt, on
   * /auth/sso-complete and held on the next visit).
   */
  answerOnly?: boolean;
}

export function LegalAcceptanceDialog({
  open,
  isSubmitting,
  errorMessage,
  onAccept,
  onDismiss,
  returnFocusTo,
  description = "Before you connect a bank, review and accept the current Terms of Service and Privacy Policy.",
  answerOnly = false,
}: LegalAcceptanceDialogProps) {
  const [agreed, setAgreed] = useState(false);

  return (
    <Dialog
      open={open}
      busy={isSubmitting}
      onOpenChange={(nextOpen) => !nextOpen && !answerOnly && onDismiss()}
    >
      <DialogContent
        showCloseButton={!answerOnly}
        onEscapeKeyDown={answerOnly ? (event) => event.preventDefault() : undefined}
        onInteractOutside={answerOnly ? (event) => event.preventDefault() : undefined}
        onCloseAutoFocus={(event) => {
          const target = returnFocusTo?.() ?? null;
          if (!canTakeFocus(target)) return;
          event.preventDefault();
          target.focus();
        }}
      >
        <DialogHeader>
          {/* Worded for both reasons this opens: the documents changed since the user
              last accepted, or they never accepted at all (an administrator-created
              account skips the signup checkbox). "Our legal documents changed" was
              false for the second case (FOLLOWUP-001 F5). */}
          <DialogTitle>Review our terms</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border/60 bg-muted/30 p-4 text-[13px] leading-relaxed">
            <Checkbox
              checked={agreed}
              onCheckedChange={(checked) => setAgreed(checked === true)}
              className="mt-0.5"
              aria-describedby={errorMessage ? "legal-acceptance-error" : undefined}
            />
            <span>
              I agree to the current{" "}
              <Link
                to="/legal/terms"
                target="_blank"
                rel="noreferrer"
                className="font-medium text-primary underline underline-offset-2"
              >
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link
                to="/legal/privacy"
                target="_blank"
                rel="noreferrer"
                className="font-medium text-primary underline underline-offset-2"
              >
                Privacy Policy
              </Link>
            </span>
          </label>
          {errorMessage && (
            <p id="legal-acceptance-error" role="alert" className="mt-3 text-xs text-destructive">
              {errorMessage}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onDismiss} disabled={isSubmitting}>
            Not now
          </Button>
          <Button type="button" onClick={onAccept} disabled={!agreed || isSubmitting}>
            {isSubmitting ? "Saving…" : "Accept and continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
